import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import NewsListCard from "@/components/feature/NewsListCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/lib/seo";
import { formatDate, getLatestNews, newsList, topics } from "@/lib/media";

const categories = ["全部", "新闻", "预告", "评论", "采访"] as const;
const PAGE_SIZE = 6;

export default function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawCategory = searchParams.get("category") ?? "全部";
  const activeCategory = (categories as readonly string[]).includes(rawCategory)
    ? rawCategory
    : "全部";
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [activeCategory]);

  usePageMeta({
    title: "影视资讯 - 最新影视动态与深度评论 - 枫与铃",
    description:
      "枫与铃影视资讯频道，提供最新的影视新闻、新片预告、深度影评与创作者采访，帮助你及时了解影视行业动态。",
    keywords: "影视资讯,影视新闻,新片预告,影评,影视采访,枫与铃",
  });

  const filtered = useMemo(() => {
    const sorted = [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1));
    if (activeCategory === "全部") return sorted;
    return sorted.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hotNews = getLatestNews(5);

  const changeCategory = (category: string) => {
    setPage(1);
    if (category === "全部") {
      setSearchParams({});
      return;
    }
    setSearchParams({ category });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-[72px]">
        <section className="border-b border-background-200 bg-background-100/80">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer hover:text-primary-600">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </span>
              <span className="text-foreground-800">影视资讯</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              影视资讯
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              新片动态、剧集前瞻、深度评论与创作者采访。我们把每天值得读的几条整理在这里。
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {categories.map((category) => {
                const count =
                  category === "全部"
                    ? newsList.length
                    : newsList.filter((item) => item.category === category).length;
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => changeCategory(category)}
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                      activeCategory === category
                        ? "bg-primary-500 font-medium text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {category}
                    <span
                      className={`text-[10px] ${
                        activeCategory === category ? "text-background-50/80" : "text-foreground-400"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <div className="grid gap-8 lg:grid-cols-[1.8fr_1fr] lg:gap-10">
            <div className="min-w-0">
              <div className="mb-4 flex items-center justify-between text-sm text-foreground-600">
                <span>
                  {activeCategory === "全部" ? "全部资讯" : activeCategory} · 共 {filtered.length} 篇
                </span>
                <span className="text-xs text-foreground-500">按发布时间排序</span>
              </div>

              {visible.length > 0 ? (
                <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                  {visible.map((item) => (
                    <NewsListCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="ri-newspaper-line"
                  title="这个分类暂时没有内容"
                  description="编辑部正在整理新的文章，先看看全部资讯吧。"
                  actionLabel="查看全部资讯"
                  onAction={() => changeCategory("全部")}
                />
              )}

              {filtered.length > PAGE_SIZE ? (
                <div className="mt-8">
                  <Pagination current={safePage} totalPages={totalPages} onChange={setPage} />
                </div>
              ) : null}
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">最近更新</h2>
                <ul className="mt-3 flex flex-col">
                  {hotNews.map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/${item.id}`}
                        className="group flex cursor-pointer gap-3 border-b border-background-200 py-3 last:border-b-0"
                      >
                        <span className="font-heading text-xs font-semibold text-primary-500">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="min-w-0">
                          <span className="line-clamp-2 block text-xs font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-[11px] text-foreground-500">
                            {formatDate(item.date)} · {item.category}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">专题合集</h2>
                <div className="mt-3 flex flex-col gap-3">
                  {topics.map((topic) => (
                    <Link
                      key={topic.id}
                      to={`/topic/${topic.id}`}
                      className="group flex cursor-pointer items-center gap-3"
                    >
                      <div className="h-14 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={topic.cover}
                          alt={topic.title}
                          title={`${topic.title} 影视专题`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {topic.title}
                        </p>
                        <p className="mt-1 line-clamp-1 text-[11px] text-foreground-500">
                          {topic.subtitle}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}